// src/pages/ManageRequest.js
// v20.2 (Use TB proxy for Hinge /person/list instead of direct Hinge list API)
// - Replaces direct hingePostJson(HINGE_BASE + "/person/list") with tbPostJson(TB_HINGE_PERSON_LIST_URL)
// - Parses TB proxy shape: { upstreamStatus, response: { personList, totalRecordCount } }
// - ENABLE_HINGE now depends on TB proxy URL presence

import AppShell from "../layout/AppShell";
import "./ManageRequest.css";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import {
    ENV,
    tbGetJson,
    tbPostJson,
    /* removed direct hingePostJson */
    /* optional */ newReqId as clientNewReqId,
} from "../api/client";
import "react-toastify/dist/ReactToastify.css";
import { formatDateMMDDYYYY } from "../utils/dateFormat";
import { isValidSearchQuery } from "../utils/searchValidation";
import { isLookupReady, useLookupReady } from "../hooks/useLookupReady";

/* ========= Constants ========= */
const CHUNK = 5000;
const MIN_PAGE_SIZE = 10;
const PREFETCH_PAGES = 3;
const MIN_SEARCH_CHARS = 4;

/** ========= ENV / CONFIG ========= */
const MANAGE_REQUESTS_URL = ENV.MANAGE_REQUESTS_URL;
const TB_SEARCH_URL = ENV.TB_SEARCH_URL;
const OFFBOARD_URL = ENV.OFFBOARD_URL; // single endpoint for both paths
const DELETE_URL = ENV.DELETE_URL;

// ✅ TB proxy for hinge person/list
const TB_HINGE_PERSON_LIST_URL =
    ENV.TB_HINGE_PERSON_LIST_URL ||
    ENV.TB_HINGE_PERSON_LIST ||
    "";

// ✅ Hinge Search proxy (same as Search page)
const TB_HINGE_SEARCH_URL =
    ENV.TB_HINGE_SEARCH_URL ||
    ENV.TB_HINGE_PERSON_SEARCH_URL ||
    ENV.TB_HINGE_SEARCH_PROXY_URL ||
    "";

/* ========= Flags ========= */
const ENABLE_HINGE = (() => {
    const v = ENV.ENABLE_HINGE;
    const on =
        typeof v === "boolean"
            ? v
            : ["true", "1", "yes"].includes(String(v || "").trim().toLowerCase());

    // ✅ require TB proxy URL instead of HINGE_BASE
    return on && !!String(TB_HINGE_PERSON_LIST_URL || "").trim();
})();

/* ========= Session ========= */
const SS =
    typeof window !== "undefined" && window.sessionStorage
        ? window.sessionStorage
        : { getItem: () => "", setItem: () => { }, removeItem: () => { } };

const K = { role: "tb_role", lookup: "tb_lookup", email: "tb_email" };

/* ========= ManageRequest state persistence ========= */
const MR_STATE_KEY = "mr.state.v1";

// ✅ NEW: allow restore ONLY when returning from details (Back)
const MR_RETURN_FROM_DETAIL_KEY = "mr.return.fromDetail";

function safeParseJson(s, fallback = null) {
    try {
        if (!s) return fallback;
        return JSON.parse(s);
    } catch {
        return fallback;
    }
}
function loadMrState() {
    return safeParseJson(SS.getItem(MR_STATE_KEY), null) || null;
}
function saveMrState(state) {
    try {
        SS.setItem(MR_STATE_KEY, JSON.stringify(state));
    } catch { }
}
function clearMrState() {
    try {
        SS.removeItem(MR_STATE_KEY);
    } catch { }
}

function getVendor() {
    const vendorId = SS.getItem("tb_vendorId") || "";
    const supplierName = SS.getItem("tb_supplierName") || "";
    return (vendorId || supplierName || "").trim();
}

function unwrapLookup(payload) {
    if (!payload || typeof payload !== "object") return null;
    if (payload.response && typeof payload.response === "object") return payload.response;
    return payload;
}

function getLookupSupplierName(payload) {
    const lookup = unwrapLookup(payload);
    if (!lookup || typeof lookup !== "object") return "";
    return (
        String(
            lookup.supplierName ||
            lookup.vendorName ||
            lookup.vendor ||
            lookup.companyName ||
            lookup.supplier_name ||
            lookup.vendor_name ||
            ""
        ).trim() || ""
    );
}

function resolveSupplierName(rawLookup) {
    // Prefer the session value; fall back to the lookup-api profile only if session is empty.
    const fromSession = getVendor();
    if (fromSession) return fromSession;
    return getLookupSupplierName(rawLookup);
}

function getActorDtvId() {
    return SS.getItem("tb_dtvid") || SS.getItem(K.email) || "";
}

function getActorEmail() {
    return SS.getItem("tb_email") || SS.getItem(K.email) || "";
}
function getActorName() {
    return (SS.getItem("tb_name") || "").trim();
}

function makeCorrelationId() {
    try {
        if (typeof clientNewReqId === "function") {
            const v = clientNewReqId();
            if (v) return String(v);
        }
    } catch { }
    try {
        if (typeof window !== "undefined" && window.tb?.newReqId) {
            const maybe = window.tb.newReqId();
            if (maybe) return String(maybe);
        }
    } catch { }
    try {
        if (typeof window !== "undefined" && window.crypto?.randomUUID)
            return window.crypto.randomUUID();
    } catch { }
    return `req_${Date.now()}`;
}

/* ========= Request memo ========= */
const TB_PROMISES = new Map();
const HINGE_PROMISES = new Map();

function memoizeOnce(map, key, factory) {
    if (map.has(key)) return map.get(key);
    const p = (async () => {
        try {
            return await factory();
        } catch (e) {
            // Never memoize failures: a transient error during navigation would otherwise be
            // reused by the next mount and show "no results" until a full page reload.
            if (map.get(key) === p) map.delete(key);
            throw e;
        } finally {
            setTimeout(() => {
                if (map.get(key) === p) map.delete(key);
            }, 8000);
        }
    })();
    map.set(key, p);
    return p;
}
function clearRequestCaches() {
    TB_PROMISES.clear();
    HINGE_PROMISES.clear();
}

function stableJSONStringifyNoCorr(obj) {
    const flat = [];
    const recur = (node, prefix = "") => {
        if (node && typeof node === "object" && !Array.isArray(node)) {
            Object.keys(node)
                .sort()
                .forEach((k) => {
                    if (k === "correlationId") return;
                    recur(node[k], prefix ? `${prefix}.${k}` : k);
                });
        } else {
            flat.push([prefix, node]);
        }
    };
    recur(obj);
    return JSON.stringify(flat);
}

/* ========= Columns ========= */
// NOTE: Per latest UI request, Manage Requests list hides Contract/PO/ID columns.
const COLUMNS = [
    { field: "full_name", label: "Worker Name", isName: true },
    { field: "vendor", label: "Vendor" },
    { field: "status", label: "Status" },
    { field: "start_date", label: "Start Date" },
    { field: "end_date", label: "End Date" },
];

const COLUMNS_EXPORT = [
    { field: "full_name", label: "Worker Name", isName: true },
    { field: "vendor", label: "Vendor" },
    { field: "status", label: "Status" },
      { field: "dtvuid", label: "DTV ID" },
      { field: "sponsor", label: "Sponsor" },
      { field: "manager", label: "Webphone Manager" },
    { field: "start_date", label: "Start Date" },
    { field: "end_date", label: "End Date" },
];

/* ========= UI helpers ========= */
const normalizeStatus = (s) =>
    String(s || "")
        .trim()
        .toLowerCase()
        .replace(/[\s_-]/g, "");
        
function statusPill(status) {
    const key = normalizeStatus(status);
    const map = {
        approved: { cls: "badge badge-ok", label: "Approved" },
        rejected: { cls: "badge badge-bad", label: "Rejected" },
        inforequested: { cls: "badge badge-warn", label: "Info Requested" },
        submitted: { cls: "badge badge-blue", label: "Submitted" },
        pending: { cls: "badge badge-blue", label: "Pending" },
        inactive: { cls: "badge badge-neutral", label: "Inactive" },
        draft: { cls: "badge badge-neutral", label: "Draft" },
        active: { cls: "badge badge-ok", label: "Active" },
    };
    return map[key] || { cls: "badge badge-neutral", label: status || "-" };
}

const rowClassForStatus = (s) =>
    normalizeStatus(s) === "inforequested" ? "row-attn" : "";

const asDateStr = (d) => {
    if (!d) return "";
    const s = String(d);
    if (s.includes("T")) return s.split("T")[0] || s;
    return s;
};

const splitContractPo = (val) => {
    const cpo = String(val || "").trim();
    if (!cpo) return { contract_number: "", po_line: "" };
    const parts = cpo.split("/").map((p) => p.trim());
    return { contract_number: parts[0] || "", po_line: parts.slice(1).join("/") || "" };
};

const dedupeCommentLines = (txt) => {
    const raw = String(txt || "");
    const lines = raw.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
    const seen = new Set();
    const out = [];
    for (const line of lines) {
        const key = line.replace(/\s+/g, " ").toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(line);
    }
    return out.join("\n").trim();
};

const normalizeCommentText = (v) => {
    if (v == null) return "";
    if (Array.isArray(v)) {
        const joined = v
            .map((x) => (x == null ? "" : String(x)))
            .map((s) => s.trim())
            .filter(Boolean)
            .join("\n");
        return dedupeCommentLines(joined);
    }
    return dedupeCommentLines(String(v).trim());
};

const mergeCommentsUnique = (a, b) => {
    const aa = normalizeCommentText(a);
    const bb = normalizeCommentText(b);
    if (!aa) return bb;
    if (!bb) return aa;
    if (aa === bb) return aa;
    if (aa.includes(bb)) return aa;
    if (bb.includes(aa)) return bb;
    return `${aa}\n${bb}`.trim();
};


/* ========= Param builders ========= */
function attachRoleParams(params) {
    const storedRole = SS.getItem(K.role) || "Nominator";
    const rawRole = storedRole.trim();
    const roleKey = rawRole.toLowerCase();

    const email = SS.getItem(K.email) || "";
    const dtvid = SS.getItem("tb_dtvid") || "";
    const vendor = getVendor();

    params.set("role", rawRole);
    params.set("loggedInUserRole", rawRole);

    if (roleKey === "nominator") {
        if (vendor) params.set("vendor", vendor);
        if (email) params.set("id", email);
    } else if (roleKey === "employeesupplier admin" || roleKey === "employee supplier admin") {
        if (email) params.set("id", email);
    } else if (roleKey === "fte nominator") {
        const id = SS.getItem("tb_dtvid") || email;
        if (id) params.set("id", id);
    } else if (roleKey === "employee sponsor") {
        const dtvId = SS.getItem("tb_dtvid") || email;
        if (dtvId) params.set("sponsor", dtvId);
    } else if (roleKey === "cp channel delegate") {
        const id = dtvid || email;
        if (id) params.set("id", id);
        if (dtvid) params.set("dtvid", dtvid);
    } else {
        const id = email || SS.getItem("tb_dtvid") || "";
        if (id) params.set("id", id);
    }
    return params;
}

function buildHingeSearchCriteriaNoMonth(storedRole, dtvId, supplierName) {
    const roleKey = (storedRole || "").toLowerCase();
    if (roleKey === "nominator") {
        const criteria = { roleName: "Nominator" };
        if (supplierName && String(supplierName).trim()) {
            criteria.userVendorName = String(supplierName).trim();
        }
        return criteria;
    }
    if (roleKey === "employeesupplier admin" || roleKey === "employee supplier admin")
        return { roleName: "Employee Supplier Admin"  /*, nominatorDtvId: dtvId || "" */ };
    if (roleKey === "fte nominator") return { roleName: "FTE Nominator", nominatorDtvId: dtvId || "" };
    if (roleKey === "employee sponsor") return { roleName: "Employee Sponsor", approverDtvId: dtvId || "" };
    if (roleKey === "cp channel delegate")
        return {
            roleName: "CP Channel Delegate",
            isCPChannelDelegate: true,
            attuidApproverDtvId: dtvId || "",
        };
    if (roleKey === "dtv ops admin" || (!storedRole && !dtvId)) return { roleName: storedRole || "DTV OPS Admin" };
    return { roleName: storedRole };
}

function buildHingeSearchIdentityParams(roleName, dtvId, supplierName) {
    const role = String(roleName || "").trim();
    const roleKey = role.toLowerCase();
    const id = String(dtvId || "").trim();
    const vendor = String(supplierName || "").trim();
    const out = {};

    if (roleKey === "nominator") {
        if (role) {
            out.role = role;
            out.searcherRoleName = role;
        }
        if (vendor) {
            out.vendor = vendor;
            out.searcherSupplierName = vendor;
        }
        if (id) out.searchDtvId = id;
        return out;
    }

    if (roleKey === "employee sponsor") {
        if (role) {
            out.role = role;
            out.loggedInUserRole = role;
            out.searcherRoleName = role;
        }
        if (id) {
            out.searchDtvId = id;
            out.sponsor = id;
            out.approverDtvId = id;
        }
        return out;
    }

    if (roleKey === "cp channel delegate") {
        if (role) {
            out.role = role;
            out.loggedInUserRole = role;
            out.searcherRoleName = role;
        }
        if (id) {
            out.searchDtvId = id;
            out.approverDtvId = id;
            out.attuidApproverDtvId = id;
        }
        return out;
    }

    // Always send the caller's role for any remaining role
    // (e.g. Employee Supplier Admin, FTE Nominator, DTV OPS Admin).
    if (role) {
        out.role = role;
        out.searcherRoleName = role;
    }
    if (id) out.searchDtvId = id;
    return out;
}

/* ========= Hinge helpers ========= */
function extractHingeTotal(res, listLength) {
    // ✅ also support TB proxy response nesting
    const payload = res?.response || res;

    return Number(
        payload?.totalRecordCount ??
        payload?.total ??
        payload?.pagination?.totalRecords ??
        payload?.pageMetadata?.total ??
        listLength ??
        0
    );
}

function normalizeHingeList(res) {
    if (!res) return [];

    // ✅ TB proxy response nesting
    const payload = res?.response || res;

    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload.personList)) return payload.personList;

    if (payload.data) {
        if (Array.isArray(payload.data.personList)) return payload.data.personList;
        if (Array.isArray(payload.data.persons)) return payload.data.persons;
    }
    if (Array.isArray(payload.persons)) return payload.persons;

    if (payload.content) {
        if (Array.isArray(payload.content.personList)) return payload.content.personList;
        if (Array.isArray(payload.content.persons)) return payload.content.persons;
    }

    return [];
}



function normalizeTbSearchArray(payload) {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (typeof payload === "string") {
        try { return normalizeTbSearchArray(JSON.parse(payload)); } catch { return []; }
    }
    const wrapped = payload?.response;
    const p = wrapped && typeof wrapped === "object" ? wrapped : payload;
    return (
        p.rows ||
        p.data ||
        p.results ||
        p.items ||
        p.personList ||
        p.personDetails ||
        p.personDetailsList ||
        []
    );
}

async function fetchTbSearchByNpwIdOnce({ q = "", roleName = "" }) {
    const base = String(TB_SEARCH_URL || "").trim();
    const needle = String(q || "").trim();
    if (!base || !needle) return [];
    const params = new URLSearchParams();
    params.set("npwId", needle);
    params.set("action", "all");

    // Match Search.jsx identity gating for TB search:
    const roleNorm = String(roleName || "").trim().toLowerCase();
    const shouldPassIdentity = ["nominator", "fte nominator", "cp channel delegate"].includes(roleNorm);

    if (shouldPassIdentity) {
        const role = String(roleName || "").trim();
        const dtvid = String(SS.getItem("tb_dtvid") || "").trim();
        const email = String(SS.getItem("tb_email") || SS.getItem(K.email) || "").trim();
        params.set("role", role);
        if (dtvid) params.set("dtvid", dtvid);
        if (email) params.set("email", email);
        if (roleNorm === "nominator") {
            const vendor = String(getVendor() || "").trim();
            if (vendor) params.set("vendor", vendor);
        }
    }

    const url = base.includes("?") ? `${base}&${params.toString()}` : `${base}?${params.toString()}`;
    const data = await tbGetJson(url);
    const arr = normalizeTbSearchArray(data);
    return Array.isArray(arr) ? arr : [];
}

/* ========= API calls ========= */
async function fetchHingePage({ page = 1, pageSize = 10, roleName, dtvId, supplierName }) {
    // ✅ TB proxy endpoint (no direct Hinge call from browser)
    const base = String(TB_HINGE_PERSON_LIST_URL || "").trim();
    if (!base) return { list: [], total: 0 };

    const searchCriteria = buildHingeSearchCriteriaNoMonth(roleName, dtvId, supplierName);

    const body = {
        correlationId: makeCorrelationId(),
        sourceSystemCode: "1",
        searchCriteria,
        pagination: { pageNumber: String(page), pageSize: String(pageSize) },
    };

    // Keep memoization but key by endpoint + stable body (excluding correlationId)
    const key = `${base}|${stableJSONStringifyNoCorr(body)}`;

    const res = await memoizeOnce(HINGE_PROMISES, key, async () => {
        // TB proxy expects POST body and returns { upstreamStatus, response: {...} }
        return tbPostJson(base, body);
    });

    const list = normalizeHingeList(res);
    const total = extractHingeTotal(res, list.length);

    return { list, total: Number(total || 0) };
}


async function fetchHingeSearchPage({ q = "", page = 1, pageSize = 10, roleName, dtvId, supplierName }) {
    const base = String(TB_HINGE_SEARCH_URL || "").trim();
    if (!base) return { list: [], total: 0 };

    const params = new URLSearchParams();
    params.set("q", String(q || "").trim());
    const identityParams = buildHingeSearchIdentityParams(roleName, dtvId, supplierName);
    Object.entries(identityParams).forEach(([k, v]) => {
        if (v != null && String(v).trim()) params.set(k, String(v).trim());
    });

    // Pagination hints (API may ignore; safe)
    params.set("page", String(page));
    params.set("pageSize", String(pageSize));

    const url = base.includes("?") ? `${base}&${params.toString()}` : `${base}?${params.toString()}`;
    const data = await tbGetJson(url);
    const payload = data?.response ?? data ?? {};
    const total = Number(payload.totalRecordCount || payload.total || payload.count || 0) || 0;
    const list = payload.personList || payload.personDetails || payload.items || payload.results || payload.data || [];
    return { list: Array.isArray(list) ? list : [], total };
}

async function fetchTBPage({ page = 1, limit = 10, sortField = "updated_at", sortOrder = "desc" }) {
    const base = ENV.MANAGE_REQUESTS_URL || MANAGE_REQUESTS_URL || "";
    const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        sortField: String(sortField),
        sortOrder: String(sortOrder),
        needCount: "true",
    });
    attachRoleParams(params);
    const url = `${base}?${params.toString()}`;
    const res = await memoizeOnce(TB_PROMISES, url, async () => tbGetJson(url));
    const rows = Array.isArray(res?.rows) ? res.rows : [];
    const total = Number(res?.total ?? rows.length ?? 0);
    return { rows, total: Number(total || 0) };
}

/* ========= Row mappers ========= */
function mapHingePersonToRow(p) {
    const first = p?.firstName ?? p?.first_name ?? "";
    const last = p?.lastName ?? p?.last_name ?? "";
    const full_name =
        `${first || ""} ${last || ""}`.trim() ||
        p?.full_name ||
        p?.name ||
        p?.dtvAnchorId ||
        p?.attuid ||
        "-";

    const created_at = p?.createdTs || p?.created_at || p?.createdAt || "";
    const updated_at = p?.updated_at || p?.updatedAt || p?.modifiedTs || p?.modified_at || p?.lastUpdated || created_at;

    return {
        _source: "HINGE",
        npw_id: undefined,

        contract_number: p?.contractNumber || p?.contract_number || "-",
        po_line: p?.poNumber || p?.po_line || "-",

        vendor: p?.supplierName || p?.vendor || p?.supplier_name || "-",
        first_name: first || "",
        last_name: last || "",
        full_name,

        role: p?.dtvRoleName || p?.role || "",
        status: p?.status || p?.personStatus || p?.approval_status || "",

        // Hinge Search may return createdTs only; list API may return effective dates
        start_date: asDateStr(p?.effectiveStartDate || p?.start_date || p?.startDate || p?.startDt || ""),
        end_date: asDateStr(p?.effectiveEndDate || p?.end_date || p?.endDate || p?.endDt || ""),
        created_at,
        updated_at,

        email: p?.primaryEmail || p?.secondaryEmail || p?.email || "",
        dtvuid: p?.dtvAnchorId || p?.dtvUid || p?.dtvid || p?.dtvId || p?.dtvuid || "",
        attuid: p?.attuid || p?.attUid || "",

        att_cp_role: p?.cinglepointRoleId || p?.att_cp_role || "",
        person_type: p?.personType || p?.person_type || "",
        sponsor: p?.employeeSponsor || p?.sponsor || "",
        manager: p?.supervisorDtvAnchorId || p?.manager ||  p?.webphone_manager_id || "",
        line: p?.lineNumber || p?.line || "",

        security_id: "-",
    };
}


function mapTBRowForUI(r) {
    const first_name = r?.first_name || r?.firstName || "";
    const last_name = r?.last_name || r?.lastName || "";
    const full_name = `${first_name} ${last_name}`.trim() || r?.full_name || r?.name || "-";

    const split = splitContractPo(r?.contract_po);

    const mergedComments = mergeCommentsUnique(r?.comments, r?.comment || r?.notes);

    const created_at = r?.created_at || r?.createdAt || r?.createdTs || r?.created || r?.updated_at || r?.updatedAt || "";
    const updated_at = r?.updated_at || r?.updatedAt || r?.modified_at || r?.modifiedAt || r?.lastUpdated || created_at;

    return {
        _source: "TB",
        ...r,

        // normalize core columns used by lists
        npw_id: r?.npw_id || r?.npwId || r?.npwID || r?.id || r?.npwId,
        first_name,
        last_name,
        full_name,

        vendor: r?.vendor || r?.supplierName || r?.supplier_name || r?.npw_manager_company || "",
        status: r?.status || r?.personStatus || r?.approval_status || "",

        start_date: asDateStr(r?.start_date || r?.startDate || r?.effectiveStartDate || r?.start_dt),
        end_date: asDateStr(r?.end_date || r?.endDate || r?.effectiveEndDate || r?.end_dt),

        created_at,
        updated_at,

        comments: mergedComments || r?.comments || r?.comment || r?.notes || "",
        contract_number: r?.contract_number || split.contract_number,
        po_line: r?.po_num || r?.po_line || split.po_line,

        email: r?.email || r?.primaryEmail || r?.secondaryEmail || "",
        dtvuid: r?.dtvuid || r?.dtvUid || r?.dtv_id || r?.dtvid || r?.dtvId || r?.dtvAnchorId || "",
        attuid: r?.attuid || r?.attUid || "",
        att_cp_role: r?.att_cp_role || "",
        person_type: r?.person_type || "",
        sponsor: r?.employeeSponsor || r?.sponsor || "",
        manager: r?.supervisorDtvAnchorId || r?.manager || r?.webphone_manager_id || "",
        line: r?.line || "",
    };
}


/* ========= Selection key helpers ========= */
function rowKey(r) {
    const npw = r?.npw_id || r?.npwId || r?.npwID || r?.id;
    if (npw != null && String(npw).trim()) return `NPW:${String(npw).trim()}`;

    const dtv = r?.dtvuid || r?.dtvUid || r?.dtvId || r?.dtvAnchorId;
    if (dtv != null && String(dtv).trim()) return `DTV:${String(dtv).trim()}`;

    const att = r?.attuid || r?.attUid;
    if (att != null && String(att).trim()) return `ATT:${String(att).trim().toUpperCase()}`;

    const email = r?.email || r?.primaryEmail || r?.secondaryEmail;
    if (email != null && String(email).trim()) return `EMAIL:${String(email).trim().toLowerCase()}`;

    const fallback = `${r?.first_name || ""}|${r?.last_name || ""}|${r?.contract_number || ""}|${r?.po_line || ""}`;
    return `ROW:${fallback}`;
}

function personKeyFromRow(r) {
    const dtv = r?.dtvuid || r?.dtvUid || r?.dtvId || r?.dtvAnchorId;
    if (dtv != null && String(dtv).trim()) return `DTV:${String(dtv).trim()}`;
    const att = r?.attuid || r?.attUid;
    if (att != null && String(att).trim()) return `ATT:${String(att).trim().toUpperCase()}`;
    const email = r?.email || r?.primaryEmail || r?.secondaryEmail;
    if (email != null && String(email).trim()) return `EMAIL:${String(email).trim().toLowerCase()}`;
    return "";
}
const isNPWKey = (k) => typeof k === "string" && k.startsWith("NPW:");

/* ========= Offboard error extraction ========= */
function safeJsonParse(maybeJson) {
    try {
        if (typeof maybeJson !== "string") return null;
        const s = maybeJson.trim();
        if (!s) return null;
        const firstBrace = s.indexOf("{");
        if (firstBrace >= 0) {
            const sliced = s.slice(firstBrace);
            return JSON.parse(sliced);
        }
        if (s.startsWith("{") || s.startsWith("[")) return JSON.parse(s);
        return null;
    } catch {
        return null;
    }
}
function extractOffboardError(err) {
    const candidates = [err?.error, err?.data, err?.body, err?.response, err?.responseBody, err?.details, err];

    let payload = null;
    for (const c of candidates) {
        if (!c) continue;

        if (c?.error && typeof c.error === "object") {
            payload = c;
            break;
        }
        if (c?.message && c?.error) {
            payload = { error: c };
            break;
        }
        const msgJson = safeJsonParse(c?.message);
        if (msgJson) {
            payload = msgJson;
            break;
        }
        const bodyJson = safeJsonParse(c?.body);
        if (bodyJson) {
            payload = bodyJson;
            break;
        }
        const rawJson = safeJsonParse(c);
        if (rawJson) {
            payload = rawJson;
            break;
        }
    }

    const apiErrObj =
        payload?.error && typeof payload.error === "object"
            ? payload.error
            : payload && typeof payload === "object"
                ? payload
                : null;

    const code = apiErrObj?.error || apiErrObj?.code || err?.code || err?.name || "";
    const msg = apiErrObj?.message || err?.message || "";
    const status = err?.status || err?.statusCode || err?.response?.status || "";

    const trimmed = String(msg || "").trim();
    const isJustHttpNumber = /^\d{3}$/.test(trimmed);

    const prettyMsg = apiErrObj?.message
        ? String(apiErrObj.message)
        : isJustHttpNumber
            ? status
                ? `Request failed (HTTP ${status}).`
                : "Request failed."
            : trimmed || (status ? `Request failed (HTTP ${status}).` : "Request failed.");

    if (code && !prettyMsg.toLowerCase().includes(String(code).toLowerCase())) {
        return `${code}: ${prettyMsg}`;
    }
    return prettyMsg;
}

/**
 * Match FTE dashboard offboard behavior (OverviewFTE):
 * TB proxy expects POST but routes internally based on payload.action.
 */
async function tbPutJson(url, bodyObj, { headers = {}, fetchInit = {} } = {}) {
    if (!url) throw new Error("URL is required");
    const safeFetchInit = { ...(fetchInit || {}) };
    if (safeFetchInit.headers) delete safeFetchInit.headers;
    return await tbPostJson(url, bodyObj ?? {}, {
        headers: headers || {},
        fetchInit: { method: "POST", ...safeFetchInit },
    });
}

/* ========= Component ========= */
export default function ManageRequest() {
    const location = useLocation();
    const navigate = useNavigate();

    const lookupReady = useLookupReady();

    const pathname = (location && location.pathname) || "";
    const isListRoute = pathname === "/manage-requests";
    const _qs = new URLSearchParams(location.search || "");
    const _hasDeepLink = Boolean(
        _qs.get("npw_id") ||
        _qs.get("npwId") ||
        _qs.get("NPWID") ||
        _qs.get("dtvid") ||
        _qs.get("dtvId") ||
        _qs.get("DTVID")
    );
    const isPureListRoute = isListRoute && !_hasDeepLink;

    // ---- Restore persisted state only if coming back from details ----
    const restoredOnceRef = useRef(false);

    // ✅ Only allow saved restore when MR_RETURN_FROM_DETAIL_KEY=1
    const allowRestoreFromDetail = (() => {
        try {
            return SS.getItem(MR_RETURN_FROM_DETAIL_KEY) === "1";
        } catch {
            return false;
        }
    })();

    const saved = allowRestoreFromDetail ? loadMrState() : null;

    const [rows, setRows] = useState([]);
    const rowsRef = useRef([]);
    useEffect(() => { rowsRef.current = rows || []; }, [rows]);
    const [fullRows, setFullRows] = useState(null);

    const tbTotalRef = useRef(0);
    const hingeTotalRef = useRef(0);
    const cachedWindowRef = useRef(0);
    // Tracks which TB/Hinge page to fetch next, and the (fixed) page size used for
    // this loading session. Without this, growing the "window" of loaded rows would
    // re-request page 1 with an ever-larger limit each time, and re-merge everything
    // from scratch. If the backend doesn't guarantee a fully stable order for tied
    // sort values across different limits, that re-fetch can come back in a slightly
    // different order each time — which is exactly what makes rows appear to jump
    // between pages. Resuming from the next unfetched page (with a fixed page size)
    // avoids re-requesting ranges we already have.
    const tbNextPageRef = useRef(1);
    const hingeNextPageRef = useRef(1);
    const tbPageSizeRef = useRef(0);
    const hingePageSizeRef = useRef(0);
    const resetPagingCursors = () => {
        tbNextPageRef.current = 1;
        hingeNextPageRef.current = 1;
        tbPageSizeRef.current = 0;
        hingePageSizeRef.current = 0;
    };
    // Freezes a stable global row order (per sort/filter sig, independent of page size) so rows
    // never reshuffle as the list streams in or when rows-per-page changes — see clientPagedRows.
    const lockedPagesRef = useRef({ sig: "", keys: [] });
    const [allRowsLoaded, setAllRowsLoaded] = useState(false);

    const [page, setPage] = useState(saved?.page && Number(saved.page) > 0 ? Number(saved.page) : 1);
    const [limit, setLimit] = useState(saved?.limit && Number(saved.limit) >= MIN_PAGE_SIZE ? Number(saved.limit) : MIN_PAGE_SIZE);

    const [sortField, setSortField] = useState(saved?.sortField || "updated_at");
    const [sortOrder, setSortOrder] = useState(saved?.sortOrder || "desc");

    const [selected, setSelected] = useState([]);
    const [loading, setLoading] = useState(false);
    // When we have shown *some* data, remaining pages continue to load in background.
    const [loadingMore, setLoadingMore] = useState(false);

    const [filters, setFilters] = useState(saved?.filters && typeof saved.filters === "object" ? saved.filters : {});
    const [filterInputValues, setFilterInputValues] = useState(saved?.filters && typeof saved.filters === "object" ? saved.filters : {});

    useEffect(() => {
        setFilterInputValues((prev) => {
            const next = { ...prev };
            Object.keys(next).forEach((k) => {
                if (!(k in (filters || {}))) delete next[k];
            });
            Object.entries(filters || {}).forEach(([k, v]) => {
                next[k] = String(v ?? "");
            });
            return next;
        });
    }, [filters]);

    // debounce filter changes so we don't call APIs on every keystroke
    const [debouncedFilters, setDebouncedFilters] = useState(filterInputValues);
    const debounceTimerRef = useRef(null);

    const getEffectiveFilters = (sourceFilters = {}) => {
        const out = {};
        const min4Fields = new Set(["full_name", "vendor"]);
        for (const [k, v] of Object.entries(sourceFilters || {})) {
            const raw = String(v ?? "").trim();
            if (min4Fields.has(k)) {
                out[k] = raw.length >= 4 && isValidSearchQuery(raw) ? raw : "";
            } else {
                out[k] = raw;
            }
        }
        return out;
    };

    useEffect(() => {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(() => {
            setDebouncedFilters(filterInputValues);
        }, 650);
        return () => {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        };
    }, [filterInputValues]);

    const debouncedFiltersClean = useMemo(() => getEffectiveFilters(debouncedFilters), [debouncedFilters]);

    const searchPrimedRef = useRef(false);
    const prevFilterCountRef = useRef(0);
    const searchLoadIdRef = useRef(0);

    const mergeUniqueByKey = (baseArr, addArr) => {
            /**
             * Merge rule:
             * - TB and Hinge rows are NOT collapsed into one row when they represent the same
             *   person — both are always shown, so the grid total matches the sum of the TB
             *   and Hinge API totals (e.g. 1228 + 1170 = 2398), with no hidden/merged rows.
             * - Still de-dupe true duplicates:
             *   - Always de-dupe by rowKey() (e.g. the same page re-fetched)
             *   - De-dupe Hinge↔Hinge repeats by personKeyFromRow() (overlapping Hinge pages)
             */
            const out = [];
            const seenRow = new Set();

            // De-dupe only Hinge duplicates (same person repeated from Hinge)
            const hingeSeenPerson = new Set();

            const pushRow = (r) => {
                const rk = rowKey(r);
                if (seenRow.has(rk)) return;

                const src = String(r?._source || "").toUpperCase();
                const isHinge = src === "HINGE";
                const pk = personKeyFromRow(r);

                if (isHinge && pk) {
                    // de-dupe hinge duplicates (same person repeated from overlapping Hinge pages)
                    if (hingeSeenPerson.has(pk)) return;
                    hingeSeenPerson.add(pk);
                }

                out.push(r);
                seenRow.add(rk);
            };

            for (const r of baseArr || []) pushRow(r);
            for (const r of addArr || []) pushRow(r);
            return out;
        };



    // When progressive paging: if fullRows is not yet populated but rows already has data,
    // seed fullRows from current rows before merging new pages.
    const mergeIntoFullRows = (addArr) => {
        setFullRows((prev) => {
            const base = prev && prev.length ? prev : [];
            return mergeUniqueByKey(base, addArr || []);
        });
    };


    const [isModalOpen, setIsModalOpen] = useState(false);
    const [note, setNote] = useState("");
    const [endDate, setEndDate] = useState("");
    const [offboardIdType, setOffboardIdType] = useState("DTVID"); // DTVID | ATTUID
    const [offboarding, setOffboarding] = useState(false);
    const [offBoardError, setOffboardError] = useState("");
    const [offBoardServerError, setOffboardServerError] = useState("");

    const rawRoleSession = (SS.getItem(K.role) || "").toLowerCase();
    const isFteNominator = rawRoleSession.trim() === "fte nominator";
    const hideVendorColumn = rawRoleSession.includes("nominator");
    const visibleColumns = useMemo(
        () => (hideVendorColumn ? COLUMNS.filter((c) => c.field !== "vendor") : COLUMNS),
        [hideVendorColumn]
    );
    const filterInputRefs = useRef({});
    const firstFilterKeyRef = useRef("");
    const lastEditedFilterKeyRef = useRef("");
    const searchSignatureRef = useRef("");

    // ✅ Persist MR state whenever it changes (only for pure list route)
    // But saving state does not imply we always restore it.
    useEffect(() => {
        if (!isPureListRoute) return;

        let cancelled = false;
        saveMrState({ page, limit, sortField, sortOrder, filters });
    }, [isPureListRoute, page, limit, sortField, sortOrder, debouncedFiltersClean]);

    // ✅ Restore ONCE only when we came back from details
    useEffect(() => {
        if (!isPureListRoute) return;

        let cancelled = false;
        if (restoredOnceRef.current) return;
        restoredOnceRef.current = true;

        // If not returning from details, force defaults and clear saved state.
        const fromDetail = allowRestoreFromDetail;

        if (!fromDetail) {
            // wipe state + inputs
            setPage(1);
            setLimit(MIN_PAGE_SIZE);
            setSortField("updated_at");
            setSortOrder("desc");
            setFilters({});
            setFullRows(null);
            searchPrimedRef.current = false;
            prevFilterCountRef.current = 0;
            clearMrState();
            try {
                SS.setItem(MR_RETURN_FROM_DETAIL_KEY, "0");
            } catch { }
            return;
        }

        // Returning from detail: restore + clear the flag
        const s = loadMrState();
        if (s) {
            if (s.page && Number(s.page) > 0) setPage(Number(s.page));
            if (s.limit && Number(s.limit) >= MIN_PAGE_SIZE) setLimit(Number(s.limit));
            if (s.sortField) setSortField(String(s.sortField));
            if (s.sortOrder) setSortOrder(String(s.sortOrder));
            if (s.filters && typeof s.filters === "object") setFilters(s.filters);
        }
        try {
            SS.setItem(MR_RETURN_FROM_DETAIL_KEY, "0");
        } catch { }
    }, [isPureListRoute, allowRestoreFromDetail]);

    // ✅ NEW: Clear filters when switching browser tabs away and coming back,
    // unless this re-entry is from detail/back (MR_RETURN_FROM_DETAIL_KEY=1).
    // NOTE: Disabled — switching browser tabs must NOT wipe the current search/list state.
    // Clearing here left the paging cursors/totals stale and produced wrong results on the
    // next search/initial load. Approvals has no such handler and behaves correctly, so we
    // match that: state persists across tab switches.

    /* ========= HARD REFRESH (post-offboard/delete) ========= */
    const refreshAfterMutation = async () => {
        clearRequestCaches();

        tbTotalRef.current = 0;
        hingeTotalRef.current = 0;
        cachedWindowRef.current = 0;
        resetPagingCursors();
        setAllRowsLoaded(false);
        searchPrimedRef.current = false;
        prevFilterCountRef.current = 0;
        // Data genuinely changed server-side (offboard/delete) — the sort/filter sig hasn't
        // changed, so the frozen order wouldn't reset on its own. Clear it explicitly so the
        // order recomputes against the fresh data instead of showing stale rows.
        lockedPagesRef.current = { sig: "", keys: [] };

        setFullRows(null);
        setRows([]);
        setSelected([]);

        setPage(1);

        const needed = Math.max(MIN_PAGE_SIZE, MIN_PAGE_SIZE);
        await fetchDefaultWindow(needed);
    };

    /* Deep-link normalization */
    useEffect(() => {
        if (!isPureListRoute) return;

        let cancelled = false;
        const qs = new URLSearchParams(location.search || "");
        const dtv = qs.get("dtvid") || qs.get("dtvId") || qs.get("DTVID");
        const npw = qs.get("npw_id") || qs.get("npwId") || qs.get("NPWID");
        if (dtv) navigate(`/manage-requests/DTVID=${encodeURIComponent(dtv)}`, { replace: true });
        else if (npw) navigate(`/manage-requests/NPWID=${encodeURIComponent(npw)}`, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, isPureListRoute]);

    /* Back/forward re-entry — clear request caches */
    const prevPathRef = useRef(location.pathname);
    useEffect(() => {
        const prev = prevPathRef.current;
        const nowPath = location.pathname;
        if (nowPath === "/manage-requests" && prev !== nowPath) {
            clearRequestCaches();
        }
        prevPathRef.current = nowPath;
    }, [location.pathname]);

    /* Default view: fetch window only when needed */
    const fetchDefaultWindow = async (sizeNeeded) => {
        // Delay hinge-list / manage-request APIs until the lookup API has responded.
        // Callers re-run via the `lookupReady` effect dep once lookup is ready. No role checks.
        if (!isLookupReady()) return;
        setLoading(true);
        try {
            const lookupRaw = SS.getItem(K.lookup) || "{}";
            let prof = {};
            try {
                prof = JSON.parse(lookupRaw);
            } catch {
                prof = {};
            }
            const storedRole = (SS.getItem(K.role) || "Nominator").trim();
            const dtvId =
                prof?.dtvAnchorId ||
                SS.getItem("tb_dtvid") ||
                SS.getItem(K.email) ||
                ENV.DEFAULT_DTVID ||
                "";
            const supplierName = resolveSupplierName(prof);

            // Prefer TB for the first render so the initial page is truly "last updated".
            // Hinge is merged in once TB settles; if TB is empty/fails, fall back to Hinge.
            let shownAny = false;
            let tbFinished = false;
            let tbHadRows = false;
            let hingeBuffered = [];
            let currentWindowRows = rowsRef.current || [];
            const showIfNeeded = () => {
                if (shownAny) return;
                shownAny = true;
                setLoading(false);
            };
            const appendRowsToWindow = (newRows) => {
                if (!newRows || !newRows.length) return;
                currentWindowRows = mergeUniqueByKey(currentWindowRows, newRows);
                if (currentWindowRows.length > (rowsRef.current || []).length) {
                    showIfNeeded();
                    setRows(currentWindowRows);
                    cachedWindowRef.current = Math.max(cachedWindowRef.current, currentWindowRows.length);
                }
            };
            const flushBufferedHinge = () => {
                if (!hingeBuffered.length) return;
                appendRowsToWindow(hingeBuffered);
                hingeBuffered = [];
            };

            const p = [];
            // Use a fixed page size for the lifetime of this loading session (established
            // once on the first fetch) and resume from the next unfetched page each time
            // the window needs to grow. This avoids re-requesting page 1 with an
            // ever-increasing limit, which can come back in a subtly different order from
            // the backend for tied sort values and make rows appear to shuffle between
            // pages as more of the list loads.
            if (!tbPageSizeRef.current) {
                tbPageSizeRef.current = Math.max(MIN_PAGE_SIZE, Math.min(CHUNK, limit * PREFETCH_PAGES));
            }
            if (!hingePageSizeRef.current) {
                hingePageSizeRef.current = Math.max(MIN_PAGE_SIZE, Math.min(CHUNK, limit * PREFETCH_PAGES));
            }
            const tbPageSize = tbPageSizeRef.current;
            const hingePageSize = hingePageSizeRef.current;
            let currentTbPage = tbNextPageRef.current;
            let effectiveTbPageSize = tbPageSize;
            let currentHingePage = hingeNextPageRef.current;
            let effectiveHingePageSize = hingePageSize;

            p.push(
                (async () => {
                    try {
                        let fetchedTbRows = 0;

                        const firstPage = await fetchTBPage({ page: currentTbPage, limit: tbPageSize, sortField, sortOrder });
                        if (!tbTotalRef.current) tbTotalRef.current = Number(firstPage.total || 0);
                        let mappedTB = (firstPage.rows || []).map(mapTBRowForUI);
                        fetchedTbRows += mappedTB.length;
                        if (mappedTB.length) {
                            tbHadRows = true;
                            effectiveTbPageSize = mappedTB.length;
                            if (hingeBuffered.length) {
                                mappedTB = mergeUniqueByKey(mappedTB, hingeBuffered);
                                hingeBuffered = [];
                            }
                            appendRowsToWindow(mappedTB);
                        }

                        const tbTotal = Number(tbTotalRef.current || 0);
                        const tbPages = effectiveTbPageSize > 0 ? Math.max(1, Math.ceil(tbTotal / effectiveTbPageSize)) : 1;
                        currentTbPage += 1;

                        while (!tbFinished && currentWindowRows.length < sizeNeeded && currentTbPage <= tbPages) {
                            const pageResult = await fetchTBPage({ page: currentTbPage, limit: tbPageSize, sortField, sortOrder });
                            const pageRows = (pageResult.rows || []).map(mapTBRowForUI);
                            if (!pageRows.length) break;
                            effectiveTbPageSize = Math.max(effectiveTbPageSize, pageRows.length);
                            fetchedTbRows += pageRows.length;
                            appendRowsToWindow(pageRows);
                            currentTbPage += 1;
                        }
                    } finally {
                        tbFinished = true;
                        flushBufferedHinge();
                    }
                })()
            );

            if (ENABLE_HINGE) {
                p.push(
                    (async () => {
                        let fetchedHingeRows = 0;

                        const firstPage = await fetchHingePage({ page: currentHingePage, pageSize: hingePageSize, roleName: storedRole, dtvId, supplierName });
                        if (!hingeTotalRef.current) hingeTotalRef.current = Number(firstPage.total || 0);
                        const mappedHinge = (firstPage.list || []).map(mapHingePersonToRow);
                        fetchedHingeRows += mappedHinge.length;
                        if (mappedHinge.length) {
                            effectiveHingePageSize = mappedHinge.length;
                            if (!tbFinished) {
                                hingeBuffered = mergeUniqueByKey(hingeBuffered, mappedHinge);
                            } else {
                                appendRowsToWindow(mappedHinge);
                            }
                        }

                        const hingeTotal = Number(hingeTotalRef.current || 0);
                        const hingePages = effectiveHingePageSize > 0 ? Math.max(1, Math.ceil(hingeTotal / effectiveHingePageSize)) : 1;
                        currentHingePage += 1;

                        while (currentWindowRows.length < sizeNeeded && currentHingePage <= hingePages) {
                            if (tbFinished && hingeBuffered.length) {
                                flushBufferedHinge();
                            }

                            const pageResult = await fetchHingePage({ page: currentHingePage, pageSize: hingePageSize, roleName: storedRole, dtvId, supplierName });
                            const pageRows = (pageResult.list || []).map(mapHingePersonToRow);
                            if (!pageRows.length) break;
                            effectiveHingePageSize = Math.max(effectiveHingePageSize, pageRows.length);
                            fetchedHingeRows += pageRows.length;
                            if (!tbFinished) {
                                hingeBuffered = mergeUniqueByKey(hingeBuffered, pageRows);
                                currentHingePage += 1;
                                continue;
                            }

                            appendRowsToWindow(pageRows);
                            currentHingePage += 1;
                        }

                        if (tbFinished && hingeBuffered.length) {
                            flushBufferedHinge();
                        }
                    })()
                );
            }

            const settled = await Promise.allSettled(p);
            const hadAnyFulfilled = settled.some((s) => s.status === "fulfilled");

            // Persist how far we've paged so the next call resumes instead of restarting.
            tbNextPageRef.current = currentTbPage;
            hingeNextPageRef.current = currentHingePage;

            const tbTotal = Number(tbTotalRef.current || 0);
            const hingeTotal = Number(hingeTotalRef.current || 0);
            const tbFullyLoaded = currentTbPage > Math.max(1, Math.ceil(tbTotal / Math.max(1, effectiveTbPageSize)));
            const hingeFullyLoaded = !ENABLE_HINGE || currentHingePage > Math.max(1, Math.ceil(hingeTotal / Math.max(1, effectiveHingePageSize)));
            if (tbFullyLoaded && hingeFullyLoaded) {
                setAllRowsLoaded(true);
            }

            // Ensure we don't stay stuck in loading when both sources return empty.
            if (!shownAny) {
                setLoading(false);
                if (!hadAnyFulfilled) toast.error("Failed to load Manage Requests.");
            }

            searchPrimedRef.current = false;
            setFullRows(null);
        } catch (e) {
            console.error("default window fetch failed:", e);
            toast.error("Failed to load Manage Requests.");
        } finally {
            // If we already rendered, this is a no-op.
            setLoading(false);
        }
    };

    // Initial and subsequent default paging — only fetch when needed (no filters)
    useEffect(() => {
        if (!isPureListRoute) return;

        const activeFilterCount = Object.values(filters).filter((v) => String(v || "").trim()).length;
        if (activeFilterCount > 0) return;

        // Once we know both sources are fully loaded, never re-trigger a fetch for this
        // sig — otherwise a persistent mismatch between the (deduped) row count and the
        // raw combined total could keep re-invoking fetchDefaultWindow indefinitely.
        if (allRowsLoaded) return;

        const windowNeeded = Math.max(MIN_PAGE_SIZE, page * limit);
        if (rows.length >= windowNeeded) return;

        // Fetch in chunks (e.g., 3 pages at a time) so we don't call APIs on every page click.
        const quantum = Math.max(MIN_PAGE_SIZE, limit * PREFETCH_PAGES);
        const quantizedNeeded = Math.max(MIN_PAGE_SIZE, Math.ceil(windowNeeded / quantum) * quantum);

        const combinedTotal = (tbTotalRef.current || 0) + (hingeTotalRef.current || 0);
        const targetSize = combinedTotal ? Math.min(quantizedNeeded, combinedTotal) : quantizedNeeded;
        if (cachedWindowRef.current >= targetSize) return;

        let cancelled = false;
        (async () => {
            try {
                setLoadingMore(true);
                await fetchDefaultWindow(targetSize);
            } finally {
                if (!cancelled) setLoadingMore(false);
            }
        })();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPureListRoute, page, limit, sortField, sortOrder, rows.length, debouncedFiltersClean, allRowsLoaded, lookupReady]);

    // NOTE: intentionally excludes `limit` — the frozen row order must stay stable when the user
    // changes rows-per-page, so only sort + filters define the order signature.
    const pageSnapshotSig = useMemo(() => {
        const entries = Object.entries(debouncedFiltersClean || {})
            .filter(([, v]) => String(v || "").trim())
            .sort((a, b) => String(a[0]).localeCompare(String(b[0])));
        const parts = entries.map(([k, v]) => `${k}=${String(v ?? "")}`);
        return `${sortField}|${sortOrder}|${parts.join("&")}`;
    }, [sortField, sortOrder, debouncedFiltersClean]);

    // When filters are cleared, reset to defaults and ensure we have at least first page cached
    useEffect(() => {
        const effectiveFilters = getEffectiveFilters(filters);
        const activeFilterCount = Object.values(effectiveFilters).filter((v) => String(v || "").trim()).length;
        if (activeFilterCount === 0) {
            if (page !== 1) setPage(1);
            if (limit !== MIN_PAGE_SIZE) setLimit(MIN_PAGE_SIZE);
            setFullRows(null);
            setRows([]);
            setSelected([]);
            tbTotalRef.current = 0;
            hingeTotalRef.current = 0;
            cachedWindowRef.current = 0;
            resetPagingCursors();
            setAllRowsLoaded(false);
            searchPrimedRef.current = false;
            firstFilterKeyRef.current = "";
            lastEditedFilterKeyRef.current = "";
            searchSignatureRef.current = "";
            const needed = MIN_PAGE_SIZE;
            fetchDefaultWindow(needed);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, lookupReady]);

    // When the sort changes in default (no-filter) mode, the already-loaded rows were
    // fetched under the previous backend order, so resuming paging from where we left
    // off would silently skip records that now belong earlier in the list. Force a
    // clean reload from page 1 under the new sort instead.
    const sortResetSkipRef = useRef(true);
    useEffect(() => {
        if (!isPureListRoute) return;
        if (sortResetSkipRef.current) {
            sortResetSkipRef.current = false;
            return;
        }
        const activeFilterCount = Object.values(getEffectiveFilters(filters)).filter((v) => String(v || "").trim()).length;
        if (activeFilterCount > 0) return;

        setFullRows(null);
        setRows([]);
        setSelected([]);
        tbTotalRef.current = 0;
        hingeTotalRef.current = 0;
        cachedWindowRef.current = 0;
        resetPagingCursors();
        setAllRowsLoaded(false);
        fetchDefaultWindow(Math.max(MIN_PAGE_SIZE, page * limit));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortField, sortOrder]);

    /* Search-mode: one-shot */
    useEffect(() => {
        if (!isPureListRoute) return;

        let cancelled = false;

        const count = Object.values(debouncedFiltersClean).filter((v) => String(v || "").trim()).length;
        const prevCount = prevFilterCountRef.current;

        // Re-run search when the primary (first) filter changes.
        const primaryKeyGuess =
            firstFilterKeyRef.current ||
            lastEditedFilterKeyRef.current ||
            Object.keys(debouncedFiltersClean).find((k) => String(debouncedFiltersClean[k] || "").trim()) ||
            "";

        const statusNeedle = String(debouncedFiltersClean.status || "").trim().toLowerCase();
        const workerNeedle = String(debouncedFiltersClean.full_name || "").trim();

        const isActiveLike =
            primaryKeyGuess === "status" &&
            statusNeedle.length >= 2 &&
            ("active".startsWith(statusNeedle) || "inactive".startsWith(statusNeedle) || "terminated".startsWith(statusNeedle));

        const primarySig = JSON.stringify({
            primaryKey: primaryKeyGuess,
            statusNeedle,
            workerNeedle,
            vendor: String(debouncedFiltersClean.vendor || "").trim(),
            start_date: String(debouncedFiltersClean.start_date || "").trim(),
            end_date: String(debouncedFiltersClean.end_date || "").trim(),
        });

        const shouldRun =
            count > 0 &&
            (!searchPrimedRef.current || searchSignatureRef.current !== primarySig || (prevCount === 0 && count > 0));

        if (shouldRun) {
            // Any filter search (worker name, vendor, start/end date, status) must wait for the
            // lookup API to finish loading. Applies to every role — no role checks.
            if (!isLookupReady()) {
                toast.warn("Search will be allowed after loading the configurations. Please wait…", {
                    toastId: "search-config-loading",
                });
                prevFilterCountRef.current = count;
                return () => {
                    cancelled = true;
                };
            }
            (async () => {
                const thisLoadId = ++searchLoadIdRef.current;
                setLoading(true);
                setLoadingMore(false);
                try {
                    const lookupRaw = SS.getItem(K.lookup) || "{}";
                    let prof = {};
                    try {
                        prof = JSON.parse(lookupRaw);
                    } catch {
                        prof = {};
                    }

                    const storedRole = (SS.getItem(K.role) || "Nominator").trim();
                    const dtvId =
                        prof?.dtvAnchorId ||
                        SS.getItem("tb_dtvid") ||
                        SS.getItem(K.email) ||
                        ENV.DEFAULT_DTVID ||
                        "";
                    const supplierName = resolveSupplierName(prof);

                    // Decide which source(s) to call based on the first filter the user typed.
                    const firstKey = primaryKeyGuess;
                    const firstIsWorker = firstKey === "full_name" && workerNeedle.length >= MIN_SEARCH_CHARS;
                    const firstIsStatus = firstKey === "status" && statusNeedle.length > 0;
                    const firstIsVendorOrDate = ["vendor", "start_date", "end_date"].includes(firstKey);

                    // Plan:
                    // 1) If first filter is Active/Inactive (or partial >=3 chars) -> call Hinge LIST only (fastest) and stream pages.
                    // 2) If first filter is Worker Name -> call Hinge SEARCH (like Search page) and filter further locally.
                    // 3) If first filter is Vendor/Dates -> call BOTH sources and stream.
                    // 4) Else (other statuses) -> call TB (manage-request) and stream.
                    const useHingeList = isActiveLike;
                    const useHingeSearch = !useHingeList && firstIsWorker;
                    const useTB = !useHingeList && !useHingeSearch && !firstIsVendorOrDate ? true : (!useHingeList && !useHingeSearch && firstIsVendorOrDate);
                    const useTBSearch = useHingeSearch;
                    const useHinge = useHingeList || useHingeSearch || firstIsVendorOrDate;

                    // We want to render as soon as *any* first page returns.
                    // Remaining pages stream in and we progressively update the table.

                    // Reset search dataset for this run.
                    setFullRows([]);
                    setRows([]);
                    setPage(1);
                    // A prior default-view load may have left this true; the page-lock
                    // logic treats allRowsLoaded as "safe to lock a short page", so it
                    // must be false while this new search's results are still streaming.
                    setAllRowsLoaded(false);

                    let shownAny = false;
                    const showIfNeeded = () => {
                        if (shownAny) return;
                        shownAny = true;
                        setLoading(false);
                        setLoadingMore(true);
                    };

                    const p1Promises = [];

                    // TB page 1
                    if (useTB) p1Promises.push(
                        (async () => {
                            const { rows: tbRows, total: tbTotal } = await fetchTBPage({ page: 1, limit: CHUNK, sortField, sortOrder });
                            if (cancelled || searchLoadIdRef.current !== thisLoadId) return;
                            if (!tbTotalRef.current) tbTotalRef.current = Number(tbTotal || 0);
                            const mappedTB = (tbRows || []).map(mapTBRowForUI);
                            if (mappedTB.length) {
                                showIfNeeded();
                                mergeIntoFullRows(mappedTB);
                                setRows((prev) => mergeUniqueByKey(prev, mappedTB));
                            }
                        })()
                    );



                    // TB search (single call, no pagination) — only when Worker Name is the first filter (match Search.jsx)
                    if (useTBSearch) p1Promises.push(
                        (async () => {
                            const tbRows = await fetchTbSearchByNpwIdOnce({ q: workerNeedle, roleName: storedRole });
                            if (cancelled || searchLoadIdRef.current !== thisLoadId) return;
                            const mapped = (tbRows || []).map(mapTBRowForUI);
                            if (mapped.length) {
                                showIfNeeded();
                                mergeIntoFullRows(mapped);
                                setRows((prev) => mergeUniqueByKey(prev, mapped));
                            }
                        })()
                    );

                    // Hinge page 1
                    if (ENABLE_HINGE && useHinge) {
                        p1Promises.push(
                            (async () => {
                                const { list: hingeList, total: hingeTotal } = useHingeSearch
                                    ? await fetchHingeSearchPage({ q: workerNeedle, page: 1, pageSize: CHUNK, roleName: storedRole, dtvId, supplierName })
                                    : await fetchHingePage({ page: 1, pageSize: CHUNK, roleName: storedRole, dtvId, supplierName });
                                if (cancelled || searchLoadIdRef.current !== thisLoadId) return;
                                if (!hingeTotalRef.current) hingeTotalRef.current = Number(hingeTotal || 0);
                                const mappedHinge = (hingeList || []).map(mapHingePersonToRow);
                                if (mappedHinge.length) {
                                    showIfNeeded();
                                    mergeIntoFullRows(mappedHinge);
                                    setRows((prev) => mergeUniqueByKey(prev, mappedHinge));
                                }
                            })()
                        );
                    }

                    const results = await Promise.allSettled(p1Promises);
                    const p1HadSuccess = results.some((r) => r.status === "fulfilled");

                    if (cancelled || searchLoadIdRef.current !== thisLoadId) return;

                    // If both sources failed (or returned empty) we show an error.
                    // Otherwise, we already rendered and we now stream the rest.
                    if (!shownAny) {
                        // toast.error("Failed to load Manage Requests.");
                        setLoading(false);
                        setLoadingMore(false);
                        return;
                    }

                    // Background: remaining pages
                    (async () => {
                        try {
                            const tbTotal = Number(tbTotalRef.current || 0);
                            const hingeTotal = Number(ENABLE_HINGE ? (hingeTotalRef.current || 0) : 0);


                            // For Worker Name searches (Hinge-search + TB-search), both APIs must be called only once.
                            // Keep normal pagination only for list/manage-request flows.
                            const allowTbPaging = Boolean(useTB) && !Boolean(useTBSearch);
                            const allowHingePaging = Boolean(ENABLE_HINGE) && Boolean(useHinge) && !Boolean(useHingeSearch);

                            const tbPages = Math.max(1, Math.ceil(tbTotal / CHUNK));
                            const hingePages = Math.max(1, Math.ceil(hingeTotal / CHUNK));

                            // TB pages 2..N
                            for (let p = 2; allowTbPaging && tbTotal > 0 && p <= tbPages; p++) {
                                if (cancelled || searchLoadIdRef.current !== thisLoadId) return;
                                await new Promise((r) => setTimeout(r, 0));
                                const { rows: pageRows } = await fetchTBPage({
                                    page: p,
                                    limit: Math.min(CHUNK, tbTotal - (p - 1) * CHUNK),
                                    sortField,
                                    sortOrder,
                                });
                                if (cancelled || searchLoadIdRef.current !== thisLoadId) return;
                                const mapped = (pageRows || []).map(mapTBRowForUI);
                                if (mapped.length) {
                                    mergeIntoFullRows(mapped);
                                    setRows((prev) => mergeUniqueByKey(prev, mapped));
                                }
                            }

                            // Hinge pages 2..N
                            if (allowHingePaging && hingeTotal > 0) {
                                for (let p = 2; p <= hingePages; p++) {
                                    if (cancelled || searchLoadIdRef.current !== thisLoadId) return;
                                    await new Promise((r) => setTimeout(r, 0));
                                    const { list } = useHingeSearch
                                        ? await fetchHingeSearchPage({
                                            q: workerNeedle,
                                            page: p,
                                            pageSize: Math.min(CHUNK, hingeTotal - (p - 1) * CHUNK),
                                            roleName: storedRole,
                                            dtvId,
                                            supplierName,
                                        })
                                        : await fetchHingePage({
                                            page: p,
                                            pageSize: Math.min(CHUNK, hingeTotal - (p - 1) * CHUNK),
                                            roleName: storedRole,
                                            dtvId,
                                            supplierName,
                                        });
                                    if (cancelled || searchLoadIdRef.current !== thisLoadId) return;
                                    const mapped = (list || []).map(mapHingePersonToRow);
                                    if (mapped.length) {
                                        mergeIntoFullRows(mapped);
                                        setRows((prev) => mergeUniqueByKey(prev, mapped));
                                    }
                                }
                            }
                        } catch (bgErr) {
                            if (!cancelled && searchLoadIdRef.current === thisLoadId) {
                                console.error("Search background load failed:", bgErr);
                                // Do not block user: we already showed some results.
                                toast.warn("Loaded partial results. Some pages failed to load.");
                            }
                        } finally {
                            if (!cancelled && searchLoadIdRef.current === thisLoadId) {
                                setLoadingMore(false);
                                setAllRowsLoaded(true);
                                searchPrimedRef.current = true;
                            }
                        }
                    })();
                } catch (e) {
                    if (!cancelled) {
                        console.error("Search full-load failed:", e);
                        toast.error("Failed to load full list for search.");
                    }
                } finally {
                    // If we already rendered from first page, do not flip loading back on.
                    if (!cancelled && searchLoadIdRef.current === thisLoadId) {
                        setLoading(false);
                    }
                }
            })();
        }


        prevFilterCountRef.current = count;

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedFiltersClean, isPureListRoute, sortField, sortOrder, lookupReady]);

    /* ===== Sorting & filtering in-memory ===== */
    const fold = (s) =>
        String(s ?? "")
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "");

    const normalizeDateNeedle = (val) => {
        const raw = String(val ?? "").trim();
        if (!raw) return "";

        const mdyDash = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
        if (mdyDash) return `${mdyDash[1]}/${mdyDash[2]}/${mdyDash[3]}`;

        const fmt = formatDateMMDDYYYY(raw);
        if (fmt && fmt !== raw) return fmt;

        return raw.replace(/-/g, "/");
    };

    const getFilterHaystack = (row, key) => {
        if (key === "start_date" || key === "end_date") return formatDateMMDDYYYY(row?.[key]);
        if (key === "status") return normalizeStatus(row?.[key]);
        return row?.[key];
    };

    const getFilterNeedle = (key, needle) => {
        if (key === "start_date" || key === "end_date") return normalizeDateNeedle(needle);
        if (key === "status") return normalizeStatus(needle);
        return needle;
    };

    const effectiveFilters = useMemo(() => getEffectiveFilters(filters), [filters]);

    const activeFilters = useMemo(() => {
        const min4Fields = new Set(["full_name", "vendor"]);
        return Object.fromEntries(
            Object.entries(effectiveFilters).filter(([k, v]) => {
                const s = String(v || "").trim();
                if (!s) return false;
                if (min4Fields.has(k) && s.length < 4) return false;
                return true;
            })
        );
    }, [effectiveFilters]);

    const sortedFilteredRows = useMemo(() => {
        const src = (fullRows && fullRows.length ? fullRows : rows) || [];

        const filtered = Object.keys(activeFilters).length
            ? src.filter((r) =>
                Object.entries(activeFilters).every(([k, needle]) =>
                    fold(getFilterHaystack(r, k)).includes(fold(getFilterNeedle(k, needle)))
                )
            )
            : src;

        const isSearchMode = Object.keys(activeFilters).length > 0;
        const dir = sortOrder === "asc" ? 1 : -1;

        // Default "last activity" ordering uses the timestamp each source actually provides:
        //  - TB (manage-request): updated_at on initial load; created_at (npw-search) while searching
        //  - Hinge (list/search): createdTs
        // On equal timestamps, TB rows rank ahead of Hinge rows.
        const isLastActivitySort = ["updated_at", "updatedat"].includes(String(sortField || "").trim().toLowerCase());
        const isHingeRow = (row) => String(row?._source || "").toUpperCase() === "HINGE";
        const activityTsRaw = (row) => {
            if (isHingeRow(row)) return row?.createdTs || row?.created_at || row?.createdAt || row?.created || "";
            return isSearchMode
                ? (row?.created_at || row?.createdAt || row?.createdTs || row?.created || "")
                : (row?.updated_at || row?.updatedAt || row?.created_at || row?.createdAt || "");
        };
        const toMs = (val) => {
            const s = String(val ?? "").trim();
            if (!s) return null;
            if (/^\d{13}$/.test(s)) return Number(s);
            if (/^\d{10}$/.test(s)) return Number(s) * 1000;
            const t = Date.parse(s);
            return Number.isNaN(t) ? null : t;
        };

        const getVal = (row) => {
            const v = row?.[sortField];
            const s = v == null ? "" : String(v);
            if (s) return s;

            const sf = String(sortField || "").trim().toLowerCase();
            if (sf === "created_at" || sf === "createdat") {
                return String(
                    row?.created_at ||
                    row?.createdAt ||
                    row?.createdTs ||
                    row?.created ||
                    row?.updated_at ||
                    row?.updatedAt ||
                    ""
                );
            }
            return "";
        };

        const tryParseTime = (val) => {
            const t = Date.parse(String(val || ""));
            return Number.isNaN(t) ? null : t;
        };

        const tryParseNum = (val) => {
            const n = Number(String(val ?? "").trim());
            return Number.isFinite(n) ? n : null;
        };

        const cmp = (a, b) => {
            if (isLastActivitySort) {
                const ma = toMs(activityTsRaw(a));
                const mb = toMs(activityTsRaw(b));
                if (ma !== mb) {
                    if (ma === null) return 1;
                    if (mb === null) return -1;
                    return ma < mb ? -1 * dir : 1 * dir;
                }
                // Equal timestamp → TB ranks ahead of Hinge.
                const aH = isHingeRow(a) ? 1 : 0;
                const bH = isHingeRow(b) ? 1 : 0;
                if (aH !== bH) return aH - bH;
                return rowKey(a).localeCompare(rowKey(b));
            }

            const va = getVal(a);
            const vb = getVal(b);

            const ta = tryParseTime(va);
            const tb = tryParseTime(vb);
            if (ta !== null || tb !== null) {
                if (ta === null) return 1;
                if (tb === null) return -1;
                if (ta !== tb) return ta < tb ? -1 * dir : 1 * dir;
            }

            const na = tryParseNum(va);
            const nb = tryParseNum(vb);
            if (na !== null || nb !== null) {
                if (na === null) return 1;
                if (nb === null) return -1;
                if (na !== nb) return na < nb ? -1 * dir : 1 * dir;
            }

            const sc = String(va).localeCompare(String(vb)) * dir;
            if (sc) return sc;

            const kc = rowKey(a).localeCompare(rowKey(b));
            if (kc) return kc;

            // TB ranks ahead of Hinge as the final tie-breaker.
            return (isHingeRow(a) ? 1 : 0) - (isHingeRow(b) ? 1 : 0);
        };

        const arr = filtered.slice();
        try {
            arr.sort(cmp);
        } catch { }
        return arr;
    }, [rows, fullRows, activeFilters, sortField, sortOrder]);


    /* ===== Export (CSV for Excel) ===== */
    const [exporting, setExporting] = useState(false);

    const csvEscape = (v) => {
        const s = v == null ? "" : String(v);
        const needsQuote = /[",\n\r\t]/.test(s);
        const out = s.replace(/"/g, '""');
        return needsQuote ? `"${out}"` : out;
    };

    const rowsToCsv = (dataRows, cols) => {
        const headers = cols.map((c) => csvEscape(c.label));
        const lines = [headers.join(",")];

        for (const r of dataRows) {
            const line = cols.map((c) => {
                const val = r?.[c.field];
                return csvEscape(val);
            });
            lines.push(line.join(","));
        }

        // Excel-friendly: UTF-8 BOM + CRLF
        return "\ufeff" + lines.join("\r\n");
    };

    const downloadTextFile = (filename, text, mime = "text/csv;charset=utf-8") => {
        try {
            const blob = new Blob([text], { type: mime });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (e) {
            console.error("download failed", e);
            toast.error("Export failed.");
        }
    };

    const loadAllForExport = async () => {
        // If we already have fullRows, use it.
        if (fullRows && fullRows.length) return fullRows;

        // If filters are active, fullRows should be present because we do a full-load for search.
        if (Object.keys(activeFilters).length) return (fullRows && fullRows.length ? fullRows : rows);

        // No filters: ensure we load everything (TB + Hinge) for accurate export.
        const lookupRaw = SS.getItem(K.lookup) || "{}";
        let prof = {};
        try { prof = JSON.parse(lookupRaw); } catch { prof = {}; }

        const storedRole = (SS.getItem(K.role) || "Nominator").trim();
        const dtvId =
            prof?.dtvAnchorId ||
            SS.getItem("tb_dtvid") ||
            SS.getItem(K.email) ||
            ENV.DEFAULT_DTVID ||
            "";
        const supplierName = resolveSupplierName(prof);

        let tbTotal = tbTotalRef.current || 0;
        let hingeTotal = ENABLE_HINGE ? (hingeTotalRef.current || 0) : 0;

        if (!tbTotal || (ENABLE_HINGE && !hingeTotal)) {
            const [{ rows: tbRows, total: tbt }, hingeRes] = await Promise.all([
                fetchTBPage({ page: 1, limit: CHUNK, sortField, sortOrder }),
                ENABLE_HINGE
                    ? fetchHingePage({ page: 1, pageSize: CHUNK, roleName: storedRole, dtvId, supplierName })
                    : Promise.resolve({ list: [], total: 0 }),
            ]);
            tbTotal = tbTotal || tbt || (tbRows?.length || 0);
            hingeTotal = hingeTotal || hingeRes.total || 0;
            if (!tbTotalRef.current) tbTotalRef.current = tbTotal;
            if (!hingeTotalRef.current) hingeTotalRef.current = hingeTotal;
        }

        const tbPages = Math.max(1, Math.ceil(tbTotal / CHUNK));
        const hingePages = Math.max(1, Math.ceil(hingeTotal / CHUNK));

        const tbAll = [];
        for (let p = 1; tbTotal > 0 && p <= tbPages; p++) {
            await new Promise((r) => setTimeout(r, 0));
            const { rows: pageRows } = await fetchTBPage({
                page: p,
                limit: Math.min(CHUNK, tbTotal - (p - 1) * CHUNK),
                sortField,
                sortOrder,
            });
            if (pageRows?.length) tbAll.push(...pageRows);
        }

        let hingeAll = [];
        if (ENABLE_HINGE && hingeTotal > 0) {
            for (let p = 1; p <= hingePages; p++) {
                await new Promise((r) => setTimeout(r, 0));
                const { list } = await fetchHingePage({
                    page: p,
                    pageSize: Math.min(CHUNK, hingeTotal - (p - 1) * CHUNK),
                    roleName: storedRole,
                    dtvId,
                    supplierName,
                });
                if (Array.isArray(list) && list.length) hingeAll = hingeAll.concat(list);
            }
        }

        const combined = tbAll.map(mapTBRowForUI).concat(hingeAll.map(mapHingePersonToRow));
        setFullRows(combined);
        setRows(combined);
        return combined;
    };

    const onExport = async () => {
        if (exporting) return;
        setExporting(true);
        try {
            let data = [];
            if (selected && selected.length > 0) {
                // Export only selected rows
                const dataset = (fullRows && fullRows.length ? fullRows : rows);
                const selectedKeys = new Set(selected.map((s) => s.key));
                data = dataset.filter((r) => selectedKeys.has(rowKey(r)));
            } else {
                // Export all (filtered or full)
                const all = await loadAllForExport();
                data = (Object.keys(activeFilters).length ? sortedFilteredRows : all) || [];
            }
            const csv = rowsToCsv(data, COLUMNS_EXPORT);
            const ts = new Date().toISOString().slice(0, 10);
           downloadTextFile(`manage-requests_${ts}.csv`, csv);
        } catch (e) {
            console.error(e);
            toast.error("Export failed.");
        } finally {
            setExporting(false);
        }
    };
    /* ===== Totals & client slicing ===== */
    const hasFilters = Object.keys(activeFilters).length > 0;

    const footerTotal = useMemo(() => {
        const sourceRows = (fullRows && fullRows.length ? fullRows : rows) || [];
        const filteredRows = hasFilters
            ? sourceRows.filter((r) =>
                Object.entries(activeFilters).every(([k, needle]) =>
                    fold(getFilterHaystack(r, k)).includes(fold(getFilterNeedle(k, needle)))
                )
            )
            : sourceRows;

        if (hasFilters) {
            return Math.max(1, filteredRows.length || 1);
        }

        const total = (tbTotalRef.current || 0) + (ENABLE_HINGE ? (hingeTotalRef.current || 0) : 0);
        if (allRowsLoaded) {
            return Math.max(1, sourceRows.length || 1);
        }
        return Math.max(1, total || sourceRows.length || 1);
    }, [hasFilters, fullRows, rows, activeFilters, allRowsLoaded]);

    const totalPages = useMemo(() => Math.max(1, Math.ceil(footerTotal / Math.max(1, limit))), [footerTotal, limit]);

    // footerTotal starts out as an optimistic raw sum of TB total + Hinge total (which can
    // double-count a person who exists in both sources) and later corrects down to the real
    // number of unique merged rows once loading finishes. If the user has already paged past
    // that corrected totalPages by the time it shrinks, they'd be stuck on a page beyond the
    // real data with "Next" disabled and nothing to show. Clamp page back into range whenever
    // totalPages shrinks below it.
    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [totalPages, page]);

    // Freeze a single global row order per sort/filter signature (independent of page size).
    // Rows already shown stay put; newly loaded rows only ever append to the unlocked tail in
    // sorted order. Because the order doesn't depend on `limit`, changing rows-per-page simply
    // re-slices the same sequence and never reshuffles what the user already saw.
    const clientPagedRows = useMemo(() => {
        const start = (page - 1) * limit;
        const end = start + limit;

        if (lockedPagesRef.current.sig !== pageSnapshotSig) {
            lockedPagesRef.current = { sig: pageSnapshotSig, keys: [] };
        }

        const byKey = new Map(sortedFilteredRows.map((r) => [rowKey(r), r]));
        const sortedKeys = sortedFilteredRows.map((r) => rowKey(r));

        // Keep the previously-frozen keys (dropping any that no longer exist), then append the
        // remaining current keys in sorted order.
        const frozen = lockedPagesRef.current.keys.filter((k) => byKey.has(k));
        const frozenSet = new Set(frozen);
        const tail = sortedKeys.filter((k) => !frozenSet.has(k));
        const order = frozen.concat(tail);

        // Extend the frozen prefix to cover the current page once those rows are settled
        // (everything loaded, or we already have a full page's worth beyond the prefix). Only
        // ever grow it — never shrink — so reducing rows-per-page can't release already-frozen
        // rows back into the (re-sortable) tail.
        const targetLen = allRowsLoaded ? order.length : (order.length >= end ? end : frozen.length);
        lockedPagesRef.current.keys = order.slice(0, Math.max(frozen.length, targetLen));

        return order
            .slice(start, end)
            .map((k) => byKey.get(k))
            .filter(Boolean)
            .map((r, idx) => ({ ...r, rowIndexName: `${r.email}-${start + idx + 1}` }));
    }, [sortedFilteredRows, page, limit, pageSnapshotSig, allRowsLoaded]);




    const startIndex = useMemo(() => {
        if (!clientPagedRows.length) return 0;
        return footerTotal > 0 ? (page - 1) * limit + 1 : 0;
    }, [clientPagedRows.length, page, limit, footerTotal]);

    const endIndex = useMemo(() => {
        if (!clientPagedRows.length) return 0;
        const remaining = Math.max(0, footerTotal - (page - 1) * limit);
        return footerTotal > 0 ? Math.min((page - 1) * limit + Math.min(limit, remaining), footerTotal) : 0;
    }, [clientPagedRows.length, page, limit, footerTotal]);

    const pageNumbers = useMemo(() => {
        const max = 7;
        let start = Math.max(1, page - Math.floor(max / 2));
        let end = start + max - 1;
        if (end > totalPages) {
            end = totalPages;
            start = Math.max(1, end - max + 1);
        }
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    }, [page, totalPages]);

    /* ===== Selection management ===== */
    // Clear selection when filters or sort change, but not on page/limit change
    useEffect(() => {
        setSelected([]);
    }, [debouncedFiltersClean, sortField, sortOrder]);


    const toggleRowSelect = (row) => {
        const key = rowKey(row);
        setSelected((prev) => {
            const exists = prev.find((r) => r.key === key);
            return exists
                ? prev.filter((r) => r.key !== key)
                : [
                    ...prev,
                    {
                        key,
                        npw_id: row.npw_id,
                        full_name: row.full_name || `${row.first_name || ""} ${row.last_name || ""}`.trim(),
                        first_name: row.first_name,
                        last_name: row.last_name,
                        status: row.status,
                        security_id: row.security_id,
                        dtvuid: row.dtvuid,
                        attuid: row.attuid,
                        email: row.email,
                    },
                ];
        });
    };

    const persistNow = () => saveMrState({ page, limit, sortField, sortOrder, filters });

    const openRow = (r) => {
        // ✅ mark: next time /manage-requests loads, we are coming from detail/back
        try {
            SS.setItem(MR_RETURN_FROM_DETAIL_KEY, "1");
        } catch { }

        persistNow();

        if (r.npw_id) {
            const id = r.npw_id;
            const draft = normalizeStatus(r.status) === "draft";
            const path = draft ? `/onboarding/new/${encodeURIComponent(id)}` : `/manage-requests/${encodeURIComponent(`NPWID=${id}`)}`;
            const here = location.pathname || "/manage-requests";
            SS.setItem("npw.search.returnPath", here);
            const from = `${location.pathname || ""}${location.search || ""}${location.hash || ""}` || here;
            navigate(path, draft ? { state: { from } } : undefined);
            return;
        }
        const dtvish = r.dtvuid || r.attuid || r.email || "";
        if (dtvish) {
            const here = location.pathname || "/manage-requests";
            SS.setItem("npw.search.returnPath", here);
            navigate(`/manage-requests/${encodeURIComponent(`DTVID=${dtvish}`)}`);
        }
    };

    const currentKeys = useMemo(() => clientPagedRows.map((r) => rowKey(r)), [clientPagedRows]);
    const allChecked = currentKeys.length > 0 && currentKeys.every((k) => selected.some((s) => s.key === k));
    const toggleAll = () => {
        if (allChecked) {
            setSelected((prev) => prev.filter((s) => !currentKeys.includes(s.key)));
        } else {
            const add = clientPagedRows.map((r) => ({
                key: rowKey(r),
                npw_id: r.npw_id,
                full_name: r.full_name || `${r.first_name || ""} ${r.last_name || ""}`.trim(),
                first_name: r.first_name,
                last_name: r.last_name,
                status: r.status,
                security_id: r.security_id,
                dtvuid: r.dtvuid,
                attuid: r.attuid,
                email: r.email,
            }));
            setSelected((prev) => {
                const seen = new Set(prev.map((x) => x.key));
                const merged = [...prev];
                add.forEach((a) => {
                    if (!seen.has(a.key)) merged.push(a);
                });
                return merged;
            });
        }
    };

    /* ===== Button enablement by status ===== */
    const oneSelected = selected.length === 1;
    const sel = oneSelected ? selected[0] : null;
    const selStatus = sel ? normalizeStatus(sel.status) : "";
    const selFullName = sel?.full_name || `${sel?.first_name || ""} ${sel?.last_name || ""}`.trim();

    const EDIT_ALLOWED_STATUSES = new Set([
        "active",
        "inactive",
        "draft",
        "approved",
        "submitted",
        "inforequested",
        "terminated",
        "submitted to cp"
        // "failed",
        //"offboarding submitted",
        //"offboarding submitted to cp"
    ]);
    const canEdit = oneSelected && EDIT_ALLOWED_STATUSES.has(selStatus);
    const canOffboardBase = oneSelected && selStatus === "active";
    const hasSelectedAttuid = !!String(sel?.attuid || sel?.attUid || "").trim();
    const canOffboard = isFteNominator ? (canOffboardBase && hasSelectedAttuid) : canOffboardBase;

    // If user previously selected ATTUID offboarding but current selected row has no ATTUID, auto-fallback to DTVID.
    useEffect(() => {
        if (isFteNominator) return;
        if (offboardIdType === "ATTUID" && !(selected?.[0]?.attuid || "")) {
            setOffboardIdType("DTVID");
        }
    }, [isFteNominator, offboardIdType, selected]);

    const statusCanDelete = [
        "draft",
        "submitted",
        "offboarding submitted",
        "offboarding submitted to cp",
        "reactivation submitted",
    ].map(normalizeStatus); const canDelete = selected.length > 0 && selected.every((s) => isNPWKey(s.key) && statusCanDelete.includes(normalizeStatus(s.status)));

    /* ---------- Render ---------- */
    const ROW_BORDER_PX = 1;
    const EXTRA_BUFFER_PX = 10;
    const viewportStyle =
        limit > MIN_PAGE_SIZE
            ? { height: `calc(var(--mr-head-h, 32px) + (${limit}) * (var(--mr-row-h, 32px) + ${ROW_BORDER_PX}px) + ${EXTRA_BUFFER_PX}px)` }
            : undefined;
    const viewportClass = `table-viewport ${limit > MIN_PAGE_SIZE ? "scroll" : "no-scroll"} table-wrap`;

    return (
        <AppShell activeKey="manage">
            <ToastContainer />
            <div className="mr-wrap mr-tight zoom-90">
                <div className="mr-titlebar compact lifted">
                    <div>
                        <div className="mr-title">Manage Requests</div>
                        <div className="mr-sub">View and manage requests for workers.</div>
                        {loadingMore && !loading ? (
                            <div style={{ marginTop: 4, fontSize: 12, color: "#6b7280" }}>
                                Loading more results…
                            </div>
                        ) : null}
                    </div>
                    <div className="mr-actions">
                        <button onClick={() => navigate("/onboarding/new")} className="btn btn-cta">
                            Start New Onboarding
                        </button>

                        <button className="btn btn-ghost" onClick={onExport} disabled={exporting || loading}>
                            {exporting ? "Exporting…" : "Export"}
                        </button>


                        <button
                            className="btn btn-ghost"
                            disabled={!canOffboard}
                            onClick={() => {
                                if (isFteNominator) setOffboardIdType("ATTUID");
                                setOffboardError("");
                                setOffboardServerError("");
                                setNote("");
                                setEndDate("");
                                if (!isFteNominator) setOffboardIdType("DTVID");
                                setIsModalOpen(true);
                            }}
                            title={isFteNominator && !hasSelectedAttuid ? "Offboarding is disabled because ATTUID is missing for this person." : ""}
                        >
                            Off Board Worker
                        </button>

                        <button
                            className="btn btn-ghost"
                            disabled={!canEdit}
                            onClick={() => {
                                if (!canEdit) return;
                                const row = (fullRows && fullRows.length ? fullRows : rows).find((r) => rowKey(r) === (sel?.key || ""));
                                if (row) openRow(row);
                            }}
                        >
                            Edit
                        </button>

                        <button
                            className="btn btn-ghost danger"
                            disabled={!canDelete}
                            onClick={async () => {
                                if (!canDelete) return;
                                const ok = window.confirm(`Are you sure you want to delete ${selected.length} item(s)?`);
                                if (!ok) return;

                                try {
                                    const ids = selected
                                        .filter((s) => isNPWKey(s.key))
                                        .map((s) => s.npw_id)
                                        .filter(Boolean);

                                    await tbGetJson({
                                        base: DELETE_URL,
                                        params: { npw_ids: ids.join(","), _: Date.now() },
                                    }, {
                                        fetchInit: { cache: "no-store" },
                                    });

                                    toast.success("Items deleted successfully.");
                                    await refreshAfterMutation();
                                } catch (err) {
                                    console.error(err);
                                    toast.error(`Error: ${err.message || "Deletion failed"}`);
                                    setSelected([]);
                                }
                            }}
                        >
                            Delete
                        </button>
                    </div>
                </div>

                <div className="card mr-card-tight">
                    <div className={viewportClass} style={viewportStyle}>
                        <table className="mr-table white-head tight zebra fixed">
                            <thead>
                                <tr className="head-row">
                                    <th className="col-select">
                                        <input type="checkbox" checked={allChecked} onChange={toggleAll} />
                                    </th>
                                    {visibleColumns.map((c) => (
                                        <th
                                            key={c.field}
                                            onClick={() => {
                                                setPage(1);
                                                if (c.field === sortField) setSortOrder((p) => (p === "asc" ? "desc" : "asc"));
                                                else {
                                                    setSortField(c.field);
                                                    setSortOrder("asc");
                                                }
                                                const inp = filterInputRefs.current[c.field];
                                                if (inp) inp.focus();
                                            }}
                                            className={`sortable ${c.field === "full_name"
                                                ? "col-name"
                                                : c.field === "status"
                                                    ? "col-status"
                                                    : c.field === "start_date" || c.field === "end_date"
                                                        ? "col-date"
                                                        : c.field === "role"
                                                            ? "col-role"
                                                            : c.field === "vendor"
                                                                ? "col-vendor"
                                                                : ""
                                                }`}
                                            title={`Sort by ${c.label}`}
                                        >
                                            <span>{c.label}</span>
                                            <span className={`sort-caret ${sortField === c.field ? (sortOrder === "asc" ? "asc" : "desc") : ""}`} />
                                        </th>
                                    ))}
                                </tr>
                                <tr className="filter-row">
                                    <th className="col-select" />
                                    {visibleColumns.map((c) => (
                                        <th key={`${c.field}-filter`} className="filter-cell">
                                            <div className="filter-input-wrap">
                                                <span className="icon">🔍</span>
                                                <input
                                                    ref={(el) => {
                                                        filterInputRefs.current[c.field] = el;
                                                    }}
                                                    className="filter-input"
                                                    type="text"
                                                    placeholder={c.field === "full_name" || c.field === "vendor" ? "Search (min 4 chars)" : "Search"}
                                                    value={filterInputValues[c.field] || ""}
                                                    onChange={(e) => {
                                                        const v = e.target.value;
                                                        const trimmed = String(v || "").trim();
                                                        const isMin4Field = c.field === "full_name" || c.field === "vendor";

                                                        setFilterInputValues((prev) => ({ ...prev, [c.field]: v }));

                                                        if (!isMin4Field) {
                                                            lastEditedFilterKeyRef.current = c.field;
                                                            if (!firstFilterKeyRef.current && trimmed) {
                                                                firstFilterKeyRef.current = c.field;
                                                            }
                                                            setPage(1);
                                                            setFilters((prev) => ({ ...prev, [c.field]: v }));
                                                            return;
                                                        }

                                                        if (!trimmed) {
                                                            lastEditedFilterKeyRef.current = c.field;
                                                            if (!firstFilterKeyRef.current && trimmed) {
                                                                firstFilterKeyRef.current = c.field;
                                                            }
                                                            setPage(1);
                                                            setFilters((prev) => ({ ...prev, [c.field]: "" }));
                                                            return;
                                                        }

                                                        if (!isValidSearchQuery(trimmed)) {
                                                            toast.warn("Please avoid special characters in your search", { toastId: "invalid-search-manage" });
                                                            return;
                                                        }

                                                        if (trimmed.length >= 4) {
                                                            lastEditedFilterKeyRef.current = c.field;
                                                            if (!firstFilterKeyRef.current && trimmed) {
                                                                firstFilterKeyRef.current = c.field;
                                                            }
                                                            setPage(1);
                                                            // Keep raw value (with spaces) so multi-word searches work;
                                                            // trimming/min-4 gating is applied in getEffectiveFilters.
                                                            setFilters((prev) => ({ ...prev, [c.field]: v }));
                                                            return;
                                                        }
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>

                            {loading ? (
                                <tbody className="tbody-loading">
                                    {Array.from({ length: limit }).map((_, i) => (
                                        <tr className="row-skeleton" key={`sk-${i}`}>
                                            <td className="col-select">
                                                <span className="skel skel-checkbox" />
                                            </td>
                                            {visibleColumns.map((c, j) => (
                                                <td key={`${i}-${j}`}>
                                                    {c.field === "status" ? <span className="skel-tag w-30" /> : <span className={`skel ${j % 2 ? "w-60" : "w-40"}`} />}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            ) : clientPagedRows.length === 0 ? (
                                <tbody>
                                    <tr>
                                        <td colSpan={visibleColumns.length + 1}>
                                            <div className="empty">No data found.</div>
                                        </td>
                                    </tr>
                                </tbody>
                            ) : (
                                <tbody>
                                    {clientPagedRows.map((r, idx) => {
                                        const pill = statusPill(r.status);
                                        const rowCls = rowClassForStatus(r.status);
                                        const key = rowKey(r);
                                        const isChecked = selected.some((s) => s.key === key);
                                        return (
                                            <tr
                                                // key={`${r._source || "ROW"}-${r.npw_id || r.email || idx}`}
                                                key={`${r.rowIndexName}`}
                                                className={rowCls}
                                                onClick={(e) => {
                                                    if (!e.target.closest("input,button,a")) openRow(r);
                                                }}
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter" || e.key === " ") openRow(r);
                                                }}
                                            >
                                                <td className="col-select" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                                                    <input type="checkbox" checked={isChecked} onChange={() => toggleRowSelect(r)} />
                                                </td>
                                                {visibleColumns.map((c) => {
                                                    const rawVal =
                                                        c.field === "full_name"
                                                            ? r.full_name || `${r.first_name || ""} ${r.last_name || ""}`.trim()
                                                            : c.field === "start_date" || c.field === "end_date"
                                                                ? formatDateMMDDYYYY(r[c.field])
                                                                : r[c.field];

                                                    return (
                                                        <td key={`${r._source || "ROW"}-${c.field}-${idx}`} className="td-trunc">
                                                            {c.field === "status" ? (
                                                                <span className="badge-wrap" title={r.status || "-"}>
                                                                    <span className={pill.cls}>{pill.label}</span>
                                                                </span>
                                                            ) : c.isName ? (
                                                                <a
                                                                    className="cell-link name-pill"
                                                                    href="#"
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        openRow(r);
                                                                    }}
                                                                    title={rawVal || "-"}
                                                                >
                                                                    {rawVal || "-"}
                                                                    <span className="source-chip"></span>
                                                                </a>
                                                            ) : (
                                                                <span className="cell-text" title={rawVal || "-"}>
                                                                    {rawVal || "-"}
                                                                </span>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            )}
                        </table>
                    </div>

                    {/* Footer */}
                    <div
                        className="mr-footer"
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "8px 12px",
                            borderTop: "1px solid #e6e8eb",
                            background: "#fff",
                        }}
                    >
                        <div className="mr-count">
                            {`${startIndex}-${endIndex} of ${footerTotal}`}
                        </div>

                        <div className="mr-pageline" style={{ display: "flex", alignItems: "center", gap: 16 }}>
                            <div className="mr-perpage inline">
                                <span>Rows per page</span>{" "}
                                <select
                                    value={limit}
                                    onChange={(e) => {
                                        const n = Math.max(MIN_PAGE_SIZE, parseInt(e.target.value, 10) || MIN_PAGE_SIZE);
                                        setLimit(n);
                                        setPage(1);
                                    }}
                                >
                                    {[10, 15, 20, 25, 30, 40, 50].map((n) => (
                                        <option key={n} value={n}>
                                            {n}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="mr-pager">
                                <button className="linklike" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
                                    ‹
                                </button>
                                {pageNumbers.map((pg) => (
                                    <button key={pg} className={`linklike ${pg === page ? "active" : ""}`} onClick={() => setPage(pg)}>
                                        {pg}
                                    </button>
                                ))}
                                <button className="linklike" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                                    ›
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Offboard modal */}
                {isModalOpen && (
                    <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="offboard-title">
                        <div className="modal">
                            {isFteNominator ? (
                                <>
                                    <h2 id="offboard-title">Off Board ATTUID of Worker</h2>
                                    <p>Are you sure you want to offboard this worker?</p>
                                </>
                            ) : (
                                <>
                                    <h2 id="offboard-title">Off Board Worker</h2>
                                    <p>Are you sure you want to offboard this worker?</p>
                                </>
                            )}

                            <div className="field">
                                <label htmlFor="offboard-worker-name">Worker Name</label>
                                <input id="offboard-worker-name" type="text" value={selFullName || ""} disabled />
                            </div>

                            <div className="field">
                                <label htmlFor="offboard-date">End Date</label>
                                <input
                                    id="offboard-date"
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    min={new Date().toISOString().split("T")[0]}
                                />
                                {offBoardError && <div className="err">{offBoardError}</div>}
                            </div>



                            {!isFteNominator && (
                                <div className="field">
                                    <label>Offboard By</label>
                                    <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
                                        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                                            <input
                                                type="checkbox"
                                                checked={offboardIdType === "DTVID"}
                                                onChange={() => setOffboardIdType("DTVID")}
                                            />
                                            <span>DTVID/ATTUID</span>
                                        </label>

                                        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                                            <input
                                                type="checkbox"
                                                checked={offboardIdType === "ATTUID"}
                                                onChange={() => setOffboardIdType("ATTUID")}
                                                disabled={!selected?.[0]?.attuid}
                                                title={!selected?.[0]?.attuid ? "ATTUID not available for selected row." : ""}
                                            />
                                            <span>ATTUID</span>
                                        </label>
                                    </div>
                                    {offboardIdType === "ATTUID" && !selected?.[0]?.attuid && (
                                        <div className="err">Selected row does not have an ATTUID.</div>
                                    )}
                                </div>
                            )}

                            <div className="field">
                                <label htmlFor="offboard-notes">Notes (optional)</label>
                                <textarea
                                    id="offboard-notes"
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    rows={3}
                                    placeholder="Add any context for the offboard request"
                                />
                            </div>

                            {offBoardServerError && <div className="err">{offBoardServerError}</div>}

                            <div className="modal-actions modal-buttons">
                                <button
                                    className="btn btn-ghost"
                                    onClick={() => {
                                        setIsModalOpen(false);
                                        setOffboardError("");
                                        setOffboardServerError("");
                                    }}
                                    disabled={offboarding}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="btn btn-cta"
                                    onClick={async () => {
                                        if (isFteNominator) {
                                            // Match FTE dashboard offboard (OverviewFTE): ATTUID-only + fixed payload shape.
                                            const today = new Date().toISOString().split("T")[0];
                                            if (!oneSelected || selStatus !== "active") {
                                                setOffboardError("Select exactly one Active row.");
                                                return;
                                            }
                                            if (!endDate) {
                                                setOffboardError("Please select an end date.");
                                                return;
                                            }
                                            if (new Date(endDate) < new Date(today)) {
                                                setOffboardError("Please select an end date greater than or equal to today.");
                                                return;
                                            }

                                            const selRow = selected[0] || {};
                                            const dtvIdToOffboard = String(selRow?.dtvuid || selRow?.dtvUid || selRow?.dtvId || selRow?.dtvAnchorId || "").trim();
                                            const attuidToOffboard = String(selRow?.attuid || selRow?.attUid || "").trim();

                                            if (!dtvIdToOffboard) {
                                                setOffboardError("Missing DTVID (anchor id).");
                                                return;
                                            }
                                            if (!attuidToOffboard) {
                                                setOffboardError("Missing ATTUID for this person. Offboarding is disabled.");
                                                return;
                                            }

                                            setOffboardError("");

                                            const actor = getActorDtvId();
                                            const actorName = getActorName();
                                            const actorEmail = getActorEmail();
                                            const performedAt = new Date().toISOString();
                                            const role = SS.getItem(K.role) || "";
                                            const name = `${selRow?.first_name || ""} ${selRow?.last_name || ""}`.trim();

                                            if (!OFFBOARD_URL) {
                                                setOffboardServerError("OFFBOARD_URL is not configured in ENV.");
                                                toast.error("OFFBOARD_URL is not configured in ENV.");
                                                return;
                                            }

                                            const payload = {
                                                end_date: endDate,
                                                comments: note ?? "",
                                                performed_by_dtv_id: actor || "",
                                                performedName: actorName || actor || "",
                                                actorEmail: actorEmail || "",

                                                submitter_id: actorEmail || undefined,
                                                submitter: actorEmail || undefined,
                                                submitter_email: actorEmail || undefined,
                                                submitter_name: actorName || actor || undefined,
                                                submitterDtvid: actor || undefined,
                                                submitter_dtvid: actor || undefined,
                                                rawActorEmail: actorEmail || undefined,
                                                performed_at: performedAt,
                                                name: name || "",
                                                action: "offboard",

                                                dtv_id: String(dtvIdToOffboard),
                                                attuid: String(attuidToOffboard),
                                                offboard_attuid: String(attuidToOffboard),

                                                role: role || "",
                                                performed_by_role: role || "",
                                                offboardIdType: "ATTUID",
                                                offboardByDtvId: false,
                                                offboardByAttuid: true,
                                            };

                                            try {
                                                setOffboardServerError("");
                                                setOffboarding(true);

                                                await tbPutJson(OFFBOARD_URL, payload);

                                                setIsModalOpen(false);
                                                toast.success("Off board request submitted");

                                                await refreshAfterMutation();
                                            } catch (e) {
                                                console.error("Offboard failed:", e);
                                                const msg = extractOffboardError(e);
                                                setOffboardServerError(msg);
                                                toast.error(msg);
                                            } finally {
                                                setOffboarding(false);
                                            }
                                            return;
                                        }

                                        if (!canOffboard) {
                                            setOffboardError("Select exactly one Active row.");
                                            return;
                                        }
                                        if (!endDate) {
                                            setOffboardError("Please select an end date.");
                                            return;
                                        }
                                        const today = new Date().toISOString().split("T")[0];
                                        if (new Date(endDate) < new Date(today)) {
                                            setOffboardError("Please select an end date greater than or equal to today.");
                                            return;
                                        }
                                        setOffboardError("");

                                        if (offboardIdType === "ATTUID" && !(selected?.[0]?.attuid || "")) {
                                            setOffboardError("Selected row does not have an ATTUID.");
                                            return;
                                        }

                                        const selRow = selected[0];
                                        const actor = getActorDtvId();
                                        const actorName = getActorName();
                                        const performedAt = new Date().toISOString();
                                        const name = `${selRow?.first_name || ""} ${selRow?.last_name || ""}`.trim();
                                        const ActorEmail = getActorEmail();
                                        try {
                                            setOffboardServerError("");
                                            setOffboarding(true);

                                            const common = {
                                                end_date: endDate,
                                                comments: note ?? "",
                                                performed_by_dtv_id: actor || "",
                                                performedName: actorName || actor || "",
                                                performed_at: performedAt,
                                                name,
                                                actorEmail: ActorEmail,
                                                submitter_id: ActorEmail || undefined,
                                                submitter: ActorEmail || undefined,
                                                submitter_email: ActorEmail || undefined,
                                                submitter_name: actorName || actor || undefined,
                                                submitterDtvid: actor || undefined,
                                                submitter_dtvid: actor || undefined,
                                                rawActorEmail: ActorEmail || undefined,
                                                action: "offboard",
                                            };

                                            const payload =
                                                selRow?.npw_id && selRow?.security_id
                                                    ? {
                                                        ...common,
                                                        npw_id: selRow.npw_id,
                                                        security_id: selRow.security_id,
                                                        offboardIdType,
                                                        offboardByDtvId: offboardIdType === "DTVID",
                                                        offboardByAttuid: offboardIdType === "ATTUID",
                                                    }
                                                    : {
                                                        ...common,
                                                        dtv_id: (offboardIdType === "ATTUID" ? (selRow?.dtvuid || "") : (selRow?.dtvuid || "")),
                                                        offboardIdType,
                                                        offboardByDtvId: offboardIdType === "DTVID",
                                                        offboardByAttuid: offboardIdType === "ATTUID",
                                                    };

                                            if (!payload.npw_id && !payload.dtv_id) {
                                                throw new Error("Selected row has neither NPWID nor DTVID to offboard by.");
                                            }

                                            await tbPostJson(OFFBOARD_URL, payload, { fetchInit: { method: "POST" } }); // PUT/POST

                                            setIsModalOpen(false);
                                            toast.success("Off board request submitted");

                                            await refreshAfterMutation();
                                        } catch (e) {
                                            console.error("Offboard failed:", e);
                                            const msg = extractOffboardError(e);
                                            setOffboardServerError(msg);
                                            toast.error(msg);
                                        } finally {
                                            setOffboarding(false);
                                        }
                                    }}
                                    disabled={offboarding || !canOffboard}
                                >
                                    {offboarding ? "Submitting..." : "Submit"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </AppShell>
    );
}
